/**
 * Sheet Component
 * Responsive sheet modal using Radix UI Dialog + Framer Motion
 * All devices: Right drawer (slides in from right)
 * iOS-style smooth transitions
 * 
 * @author Varun Pandey
 */

import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  title?: string;
  description?: string;
  /** Prevent closing by clicking/tapping outside the sheet. */
  preventOutsideClose?: boolean;
  /** Prevent closing with Escape key. */
  preventEscapeClose?: boolean;
}

// iOS-optimized easing curves
const iosEasing: [number, number, number, number] = [0.25, 0.1, 0.25, 1.0];
const iosExitEasing: [number, number, number, number] = [0.4, 0.0, 1, 1];

// Right drawer variants (all devices)
const rightDrawerVariants = {
  closed: {
    x: '100%',
    transition: {
      duration: 0.3,
      ease: iosExitEasing,
    },
  },
  open: {
    x: 0,
    transition: {
      duration: 0.35,
      ease: iosEasing,
    },
  },
};

// Overlay variants with subtle blur (match app sheets)
const overlayVariants = {
  closed: {
    opacity: 0,
    backdropFilter: 'blur(0px)',
    transition: {
      duration: 0.2,
    },
  },
  open: {
    opacity: 1,
    backdropFilter: 'blur(8px)',
    transition: {
      duration: 0.3,
    },
  },
};

export const Sheet: React.FC<SheetProps> = ({
  open,
  onOpenChange,
  children,
  title,
  description,
  preventOutsideClose = false,
  preventEscapeClose = false,
}) => {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            {/* Overlay */}
            <Dialog.Overlay asChild>
              <motion.div
                variants={overlayVariants}
                initial="closed"
                animate="open"
                exit="closed"
                className="fixed inset-0 z-50 bg-black/50"
                style={{
                  willChange: 'opacity, backdrop-filter',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                }}
              />
            </Dialog.Overlay>

            {/* Sheet: right drawer on all devices */}
            <Dialog.Content
              asChild
              forceMount
              onPointerDownOutside={(event) => {
                if (preventOutsideClose) event.preventDefault();
              }}
              onInteractOutside={(event) => {
                if (preventOutsideClose) event.preventDefault();
              }}
              onEscapeKeyDown={(event) => {
                if (preventEscapeClose) event.preventDefault();
              }}
            >
              <motion.div
                variants={rightDrawerVariants}
                initial="closed"
                animate="open"
                exit="closed"
                className={cn(
                  'fixed z-50 flex flex-col gap-4 bg-[#1C1C1E]/60 backdrop-blur-xl p-6 shadow-lg',
                  // Right drawer positioning/sizing for all breakpoints
                  'inset-y-0 right-0 left-auto',
                  'w-[90vw] max-w-[90vw] lg:w-full lg:max-w-md rounded-none rounded-l-[1.5rem] border-l border-[#3A3A3C]',
                  'h-[var(--app-height,100dvh)] max-h-[var(--app-height,100dvh)] overflow-y-auto overscroll-y-contain max-lg:scroll-pb-24'
                )}
                data-sheet-scroll-container="true"
                data-ptr-ignore
                style={{
                  willChange: 'transform',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                  WebkitTransform: 'translateZ(0)',
                  transform: 'translateZ(0)',
                }}
              >
                {(title || description) && (
                  <div className="mb-6">
                    {title && (
                      <Dialog.Title className="text-2xl font-bold text-white mb-2">
                        {title}
                      </Dialog.Title>
                    )}
                    {description && (
                      <Dialog.Description className="text-sm text-[#8E8E93]">
                        {description}
                      </Dialog.Description>
                    )}
                  </div>
                )}
                <Dialog.Close
                  className={cn(
                    'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity',
                    'hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                    'disabled:pointer-events-none'
                  )}
                >
                  <X className="h-5 w-5 text-white" />
                  <span className="sr-only">Close</span>
                </Dialog.Close>
                <div className=" w-full">
                  {children}
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
};
